import { useCallback, useEffect, useMemo, useState } from "react";
import { DataSources, ExportAccountsOptions, ModalSize } from "types/enums";
import { ExportCompany, UrlResponse } from "../../../../types/apiTypes";
import { SerializedError } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import { CustomToast } from "../../CustomToast";
import { getErrorMessage } from "../../../../utils/getErrorMessage";
import { INPUT_REGEX } from "../../../../utils/regex";
import { ReuseModal } from "components/ui/ReuseModal";
import { Box, Stack, Typography } from "@mui/material";
import { SubBlock } from "../../export-data/SubBlock";
import { SelectedExportOptions } from "../../export-data/SelectedExportOption";
import { SelectCompanies } from "../../export-data/SelectCompanies";
import { ReuseInput } from "../../ReuseInput";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { openLinkHandler } from "../../../../utils/openLinkHandler";
import { SelectDataSource } from "../../export-data/SelectDataSource";

const DEFAULT_FILE_NAME = "Export-data";

type Props = {
  title: string;
  submitBtnText: string;
  isLoading: boolean;
  items: ExportCompany[];
  dataSourceList: DataSources[];
  onClose: () => void;
  onSubmit: (
    ids: string[],
    fileName: string,
    isSelectedAll: boolean,
    data_source: DataSources[],
  ) => Promise<UrlResponse>;
};

export const ExportCSVModal = ({
  title,
  submitBtnText,
  isLoading,
  items,
  onClose,
  onSubmit,
  dataSourceList,
}: Props) => {
  const [isError, setIsError] = useState<boolean>(false);
  const [fileName, setFileName] = useState<string>(DEFAULT_FILE_NAME);
  const [dataSources, setDataSources] = useState<DataSources[]>([]);
  const [option, setOption] = useState<ExportAccountsOptions>(
    ExportAccountsOptions.all,
  );
  const [value, setValue] = useState<string>("");
  const [selectedItemList, setSelectedItemList] = useState<string[]>([]);
  const [analyzedItemList, setAnalyzedItemList] = useState<string[]>([]);

  const isSelectedAll = option === ExportAccountsOptions.all;

  useEffect(() => {
    if (isSelectedAll) {
      setValue("");
      setSelectedItemList(analyzedItemList);
    }
  }, [option]);

  useEffect(() => {
    const analyzedList: string[] = [];
    items.forEach(({ is_analyzed, id }) => {
      if (is_analyzed) analyzedList.push(id);
    });
    setSelectedItemList(analyzedList);
    setAnalyzedItemList(analyzedList);
  }, [items]);

  const handleChange = useCallback(
    (item: string) => {
      setSelectedItemList((prev) => {
        const isInList = prev.includes(item);
        if (isInList) {
          return prev.filter((id) => id !== item);
        } else {
          return [...prev, item];
        }
      });
    },
    [setSelectedItemList],
  );

  const selectAllHandler = () => {
    if (!items) return setSelectedItemList([]);
    setSelectedItemList(
      analyzedItemList.length === selectedItemList.length
        ? []
        : analyzedItemList,
    );
  };

  const successHandler = ({ url }: UrlResponse) => {
    openLinkHandler(url, fileName);
    onClose();
  };

  const errorHandler = (
    error: FetchBaseQueryError | SerializedError | undefined,
  ) => {
    toast.error(
      <CustomToast
        type={"error"}
        text={
          getErrorMessage(error).globalError ||
          "Something went wrong. Please try again"
        }
        title={"Export Data Failed"}
      />,
    );
  };

  const submitHandler = async () => {
    onSubmit(selectedItemList, fileName, isSelectedAll, dataSources)
      .then(successHandler)
      .catch(errorHandler);
  };

  const inputChangeHandler = (value: string) => {
    setIsError(!value.trim() || !INPUT_REGEX.test(value));
    setFileName(value);
  };

  const isDisabled =
    (selectedItemList.length === 0 &&
      option === ExportAccountsOptions.selected) ||
    (ExportAccountsOptions.source === option && dataSources.length === 0);

  const mapper = useMemo(
    () => ({
      [ExportAccountsOptions.selected]: (
        <SelectCompanies
          value={value}
          analyzedItemList={analyzedItemList}
          selectedItemList={selectedItemList}
          companyList={items || []}
          handleChange={handleChange}
          selectAllHandler={selectAllHandler}
          setValue={setValue}
        />
      ),
      [ExportAccountsOptions.source]: (
        <SelectDataSource
          items={dataSourceList}
          setSourcesHandler={setDataSources}
        />
      ),
      [ExportAccountsOptions.all]: null,
    }),
    [selectedItemList],
  );

  return (
    <ReuseModal
      isOpen={true}
      isLoading={isLoading}
      onClose={onClose}
      title={title}
      onSubmit={submitHandler}
      maxWidth={ModalSize.small}
      submitBtnColor="#1C3C6C"
      submitBtnText={submitBtnText}
      isDisabled={isDisabled || isLoading || isError}
      isDisabledCancelBtn={isDisabled || isLoading || isError}
    >
      <Stack gap="12px" paddingTop={"8px"}>
        <Typography
          component="span"
          variant="subtitle1"
          color="custom.textDarkBlue"
          fontWeight={500}
        >
          Which data set of discovered buying committee members you would like
          to export?
        </Typography>
        <SelectedExportOptions
          option={option}
          setOption={setOption}
          isSelectedAll={selectedItemList.length === analyzedItemList.length}
          selectedItemList={selectedItemList}
          hideDataSource={dataSourceList.length <= 1}
        />
        {mapper[option]}
        <SubBlock title="File Name" text="What should be the CSV file name?" />
        <Stack
          direction="row"
          gap="8px"
          alignItems="center"
          pt="16px"
          paddingBottom="35px"
        >
          <Box
            sx={{
              position: "relative",
              width: "100%",
            }}
          >
            <ReuseInput
              name="name"
              handleInputChange={(key, value) => inputChangeHandler(value)}
              errorMessage={
                "Must contain only letters, numbers, and basic punctuation"
              }
              value={fileName}
              maxLength={50}
              label="File Name*"
              placeholder=" e.g., INFUSE"
              isError={isError}
              setErrorHandler={(key, value) => setIsError(value)}
            />
          </Box>

          <Typography
            component="span"
            variant="body1"
            sx={{ fontWeight: 500, lineHeight: "26px" }}
            color="#4C545B"
          >
            .csv
          </Typography>
        </Stack>
      </Stack>
    </ReuseModal>
  );
};
